import { BoundaryBoxCoordinates, PositionsListMap } from "features/map/types"
import { cloneDeep, isDefined } from "nodash"
import { HotspotWithContext } from "@nova/constants"
import { Perimeter } from "@nova/protobuf"

const initialPositionsListMap = {
    latitudes: [] as number[],
    longitudes: [] as number[],
} as PositionsListMap

/**
 * collects relevant coordinate points from all hotspot entities passed via allItems
 *
 * @todo -- this approach does not currently consider filtered states. it ignores deleted hotspots and includes all out/active status items.
 *       -- in the future, we should build in possibly more flexibility
 * @returns PositionsListMap
 */
export const reduceLayeredHotspotsToCoordinatePointsMap = ({
    allItems,
}: {
    allItems: HotspotWithContext[]
}): PositionsListMap => {
    const result = allItems.reduce((result, hotspot) => {
        // note that we're only grabbing the actual coordinate data, not the rest of the entity
        const { longitude, latitude } = hotspot

        if (!longitude || !latitude) {
            console.warn("Encountered a hotspot without center point!", { hotspot })
            return result
        }

        result.longitudes.push(longitude)
        result.latitudes.push(latitude)

        return result
    }, cloneDeep(initialPositionsListMap))

    return result
}

export const reduceLayeredPerimeterToCoordinatePointsMap = ({
    perimeter,
}: {
    perimeter?: Perimeter
}): PositionsListMap => {
    const result = cloneDeep(initialPositionsListMap)

    // note that we're only grabbing the actual coordinate data, not the rest of the entity

    if (!perimeter) {
        return result
    }

    // because of legacy flaws, we need to carefully extract the coordinate pairs
    const perimeterPolygon = perimeter.polygons

    perimeterPolygon.forEach((polygon) => {
        polygon.coordinates.forEach((coordinatePair) => {
            const longitude = coordinatePair.longitude
            const latitude = coordinatePair.latitude

            if (longitude === undefined || latitude === undefined) {
                throw "Failed to parse perimeter polygon"
            }

            result.longitudes.push(longitude as number)
            result.latitudes.push(latitude as number)
        })
    })

    return result
}

export const reduceAllPointsToBoundaryBox = (
    items: PositionsListMap[]
): BoundaryBoxCoordinates | undefined => {
    if (!items.length) {
        return undefined
    }

    // take a list of maps and merge them into one large map
    const mergedPointsMap = items.reduce(
        (result, item) => {
            const { latitudes, longitudes } = item

            result.latitudes.push(...latitudes)
            result.longitudes.push(...longitudes)

            return result
        },
        { latitudes: [] as number[], longitudes: [] as number[] }
    )

    const { latitudes, longitudes } = mergedPointsMap
    if (latitudes.some(isDefined) && longitudes.some(isDefined)) {
        // then grab the boundaries of that giant point list - note that for type safety we added zeros when undefined, but
        // because of the check above, we should never get to that point!
        return {
            minLatitude: Math.min(...latitudes) || 0,
            minLongitude: Math.min(...longitudes) || 0,
            maxLatitude: Math.max(...latitudes) || 0,
            maxLongitude: Math.max(...longitudes) || 0,
        }
    }

    return undefined
}

type BoundaryBoxReducers = typeof reduceLayeredPerimeterToCoordinatePointsMap

export const getBoundaryBoxForSingleItem =
    (reducer: BoundaryBoxReducers) => (perimeter: Perimeter) => {
        const coordinatesMap = reducer({
            perimeter,
        })

        const boundaryBox = reduceAllPointsToBoundaryBox([coordinatesMap])

        return boundaryBox
    }

export const getBoundaryBoxForSinglePerimeter = getBoundaryBoxForSingleItem(
    reduceLayeredPerimeterToCoordinatePointsMap
)
